// Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { ethers, JsonRpcProvider, Network } from 'ethers'

import { ContractCTO } from './ContractCTO'
import {
  DEV_LYT_STAGE_CONFIG,
  DEV_STAGE_CONFIG,
  DEVOTTO_STAGE_CONFIG,
  DEVTOM_STAGE_CONFIG,
} from './DeploymentConfigsDev'
import { LOCAL_LYT_STAGE_CONFIG, LOCAL_STAGE_CONFIG, LOCAL_STAGE_CONFIG_PROD_CANARY } from './DeploymentConfigsLocal'
import {
  PROD_CANARY_LYT_STAGE_CONFIG_FB,
  PROD_CANARY_STAGE_CONFIG,
  PROD_LYT_STAGE_CONFIG_FB,
  PROD_STAGE_CONFIG,
} from './DeploymentConfigsProd'
import { QA_LYT_STAGE_CONFIG, QA_STAGE_CONFIG, QA_LYT_STAGE_CONFIG_FB } from './DeploymentConfigsQa'
import { SANDBOX_CANARY_STAGE_CONFIG, SANDBOX_STAGE_CONFIG } from './DeploymentConfigsSandbox'
import { ChainConfigName, ChainConfiguration, ChainName, StageConfiguration } from './DeploymentConfigTypes'
import { ChainId, StageName } from './Enums'

// PROVIDERS
export const LOCAL_HH_URL = 'http://127.0.0.1:8545'

export const WALLET_CONNECT_PROJECT_ID = '95a7421b836a16c2c5b981ab3393a4ca'

const PLUME_DEFAULT_MAINNET_URL = 'https://rpc.plume.org'
const SEPOLIA_DEFAULT_URL = 'https://eth-sepolia.g.alchemy.com/v2/ZfYMU4EmOzmizBu2ybqI8'
const FUJI_DEFAULT_URL = 'https://avax-fuji.g.alchemy.com/v2/4cSE9_-9aE5rv6QJtMm9U'
const SEPOLIA_JEST_URL = 'https://eth-sepolia.g.alchemy.com/v2/0yaiy8KJZuYR87vj9PqNTX2BtjXQiYMe'

export const AVALANCHE_DEFAULT_URL = 'https://avax-mainnet.g.alchemy.com/v2/p5dgOkAqxryvxryB4To24'
export const MAINNET_ALCHEMY_URL = 'https://eth-mainnet.g.alchemy.com/v2/QJqYiOEGbhFQtwKjJUs03'
export const MAINNET_INFURA_URL = 'https://mainnet.infura.io/v3/130c27d6ad294a73a69cf40c20dda0eb'

let hardhatProvider: JsonRpcProvider | undefined
export const chainIdToProvider = (chainId: ChainId): JsonRpcProvider => {
  if (chainId == ChainId.HARDHAT) {
    if (!hardhatProvider) {
      hardhatProvider = new ethers.JsonRpcProvider(LOCAL_HH_URL, new Network('Hardhat', chainId.toString()), {
        staticNetwork: true,
        pollingInterval: 10,
        polling: true,
        batchMaxCount: 1,
      })
    }
    return hardhatProvider
  }

  if (chainId == ChainId.SEPOLIA) {
    return new ethers.JsonRpcProvider(SEPOLIA_JEST_URL, new Network('Sepolia', chainId.toString()), {
      staticNetwork: true,
      pollingInterval: 100,
      polling: false,
      batchMaxCount: 1,
    })
  }
  if (chainId == ChainId.MAINNET) {
    return new ethers.JsonRpcProvider(MAINNET_ALCHEMY_URL, new Network('Mainnet', chainId.toString()), {
      staticNetwork: true,
      pollingInterval: 100,
      polling: false,
      batchMaxCount: 1,
    })
  }
  if (chainId == ChainId.FUJI) {
    return new ethers.JsonRpcProvider(FUJI_DEFAULT_URL, new Network('Fuji', chainId.toString()), {
      staticNetwork: true,
      pollingInterval: 100,
      polling: false,
      batchMaxCount: 1,
    })
  }
  if (chainId == ChainId.AVALANCHE) {
    return new ethers.JsonRpcProvider(AVALANCHE_DEFAULT_URL, new Network('Avalanche', chainId.toString()), {
      staticNetwork: true,
      pollingInterval: 100,
      polling: false,
      batchMaxCount: 1,
    })
  }
  if (chainId == ChainId.PLUME_MAINNET) {
    return new ethers.JsonRpcProvider(PLUME_DEFAULT_MAINNET_URL, new Network('Plume', chainId.toString()), {
      staticNetwork: true,
      pollingInterval: 100,
      polling: false,
      batchMaxCount: 1,
    })
  }
  if (chainId == ChainId.PLUME_TESTNET) {
    return new ethers.JsonRpcProvider('https://testnet-rpc.plume.org/', new Network('Plume', chainId.toString()), {
      staticNetwork: true,
      pollingInterval: 100,
      polling: false,
      batchMaxCount: 1,
    })
  }
  throw new Error(`Unknown chainId ${chainId}`)
}

export const chainIdToRpcUrl = (chainId: number): string => {
  switch (chainId) {
    case ChainId.FUJI:
      return FUJI_DEFAULT_URL
    case ChainId.SEPOLIA:
      return SEPOLIA_DEFAULT_URL
    case ChainId.AVALANCHE:
      return AVALANCHE_DEFAULT_URL
    case ChainId.PLUME_MAINNET:
      return PLUME_DEFAULT_MAINNET_URL
    case ChainId.MAINNET:
      return MAINNET_ALCHEMY_URL
    case ChainId.HARDHAT:
      return LOCAL_HH_URL
    default:
      throw new Error(`chainIdToRpcUrl: chainId ${chainId} not supported`)
  }
}

export const BLOCK_EXPLORER_API_CONFIG: Record<string, { url: string; apiKey: string }> = {
  mainnet: {
    url: 'https://api.etherscan.io/api',
    apiKey: process.env.ETHERSCAN_API_KEY_MAINNET || 'AMHP1YPT8IFGNH3AGDMM8XIQPRTW4TFV2A',
  },
  sepolia: {
    url: 'https://api-sepolia.etherscan.io/api',
    apiKey: process.env.ETHERSCAN_API_KEY_SEPOLIA || 'AMHP1YPT8IFGNH3AGDMM8XIQPRTW4TFV2A',
  },
  fuji: {
    url: 'https://api.routescan.io/v2/network/testnet/evm/43113/etherscan/api',
    apiKey: process.env.ETHERSCAN_API_KEY_FUJI || '',
  },
  avalanche: {
    url: 'https://api.routescan.io/v2/network/mainnet/evm/43114/etherscan/api',
    apiKey: process.env.ETHERSCAN_API_KEY_AVALANCHE || '',
  },
  base: {
    url: 'https://api.basescan.org/api',
    apiKey: process.env.ETHERSCAN_API_KEY_BASE || 'AMHP1YPT8IFGNH3AGDMM8XIQPRTW4TFV2A',
  },
  //TODO: Missing for plume
}
export const findChildStageConfigurations = (parentStageConfiguration: StageConfiguration): StageConfiguration[] => {
  return STAGE_CONFIGURATIONS.filter((stageConfig) => {
    return stageConfig.parentStageConfiguration?.stageName === parentStageConfiguration.stageName
  })
}
export function chainConfigurationToProvider(chainConfiguration: ChainConfiguration): JsonRpcProvider {
  if (process.env.AWS_LAMBDA_FUNCTION_NAME) {
    return new ethers.JsonRpcProvider(chainConfiguration.backendProviderUrl, undefined, { staticNetwork: true })
  }
  return new ethers.JsonRpcProvider(chainConfiguration.frontendProviderUrl, undefined, { staticNetwork: true })
}

export const findStageConfiguration = (stageName: StageName): StageConfiguration => {
  const stageConfig = STAGE_CONFIGURATIONS.find((dc) => {
    if (dc.stageName.toLowerCase() === stageName.toLowerCase()) {
      return true
    }

    return false
  })

  if (stageConfig) {
    return stageConfig
  }

  throw new Error('findStageConfiguration: unknown stageName: ' + stageName)
}

export const getChainConfigurationFromCto = (cto: ContractCTO): ChainConfiguration => {
  const stage = process.env.stage as StageName
  const stageConfig = findStageConfiguration(stage)
  const chainConfig = stageConfig.chainConfigurations.find((cc) => cc.chainConfigurationName === cto.chainConfigName)
  if (!chainConfig) {
    throw new Error(`getChainConfigurationFromCto: unknown chainConfigName: ${cto.chainConfigName}`)
  }
  return chainConfig
}

export const findLytStageConfigurationForParentStage = (parentStageName: StageName): StageConfiguration | undefined => {
  const lytStageConfig = STAGE_CONFIGURATIONS.find((stageConfig) => {
    if (stageConfig.parentStageConfiguration?.stageName.toLowerCase() === parentStageName.toLowerCase()) {
      return true
    }
    return false
  })
  return lytStageConfig
}
export const findChainConfiguration = (chainConfigurationName: ChainConfigName): ChainConfiguration | undefined => {
  const stageConfig = findStageConfiguration(process.env.stage as StageName)
  const chainConfig = stageConfig.chainConfigurations.find((cc) => cc.chainConfigurationName === chainConfigurationName)
  return chainConfig
}

export const findChainConfigurationByChainName = (chainName: ChainName): ChainConfiguration => {
  const stageConfig = findStageConfiguration(process.env.stage as StageName)
  const chainConfig = stageConfig.chainConfigurations.find((cc) => cc.chainName === chainName)
  if (!chainConfig) {
    throw new Error('findChainConfigurationByChainName: unknown chainName: ' + chainName)
  }
  return chainConfig
}

export const findChainConfigurationByChainId = (chainId: number): ChainConfiguration => {
  const stageConfig = findStageConfiguration(process.env.stage as StageName)

  const chainConfig = stageConfig.chainConfigurations.find((cc) => cc.chainId === chainId)
  if (chainConfig) {
    return chainConfig
  }

  throw new Error('findChainConfigurationByChainId: unknown chainId: ' + chainId)
}

export const convertToAppKitNetwork = (chainConfig: ChainConfiguration): any => {
  const getBlockExplorerName = (chainName: string) => {
    switch (chainName) {
      case 'Avalanche':
      case 'Fuji':
        return 'SnowTrace'
      case 'Plume':
        return 'Plume Explorer'
      default:
        return 'Etherscan'
    }
  }

  return {
    id: chainConfig.chainId,
    name: chainConfig.chainName,
    nativeCurrency: chainConfig.nativeCurrency,
    rpcUrls: {
      default: { http: [chainConfig.frontendProviderUrl] },
    },
    blockExplorers: {
      default: {
        name: getBlockExplorerName(chainConfig.chainName),
        url: chainConfig.blockExplorerUrl || '',
      },
    },
    contracts: chainConfig.contracts,
    testnet: chainConfig.isTestnet,
  }
}

export function chainConfigurationNameToChainConfiguration(
  chainConfigurationName: ChainConfigName
): ChainConfiguration {
  for (const stageConfig of STAGE_CONFIGURATIONS) {
    const chainConfig = stageConfig.chainConfigurations.find(
      (cc) => cc.chainConfigurationName === chainConfigurationName
    )
    if (chainConfig) {
      return chainConfig
    }
  }
  throw new Error('findChainConfigurationByChainId: unknown chainConfigurationName: ' + chainConfigurationName)
}

export const STAGE_CONFIGURATIONS: StageConfiguration[] = [
  LOCAL_STAGE_CONFIG,
  LOCAL_STAGE_CONFIG_PROD_CANARY,

  DEV_STAGE_CONFIG,
  DEVOTTO_STAGE_CONFIG,
  DEVTOM_STAGE_CONFIG,

  QA_STAGE_CONFIG,

  SANDBOX_STAGE_CONFIG,
  SANDBOX_CANARY_STAGE_CONFIG,

  PROD_STAGE_CONFIG,
  PROD_CANARY_STAGE_CONFIG,

  LOCAL_LYT_STAGE_CONFIG,
  DEV_LYT_STAGE_CONFIG,
  QA_LYT_STAGE_CONFIG,
  QA_LYT_STAGE_CONFIG_FB,

  PROD_CANARY_LYT_STAGE_CONFIG_FB,

  PROD_LYT_STAGE_CONFIG_FB,
]
